// Internal orients: horizontal separates along x (merchandising vertical blocks).
import { Orientation, SpineLevel, StudioDraft } from "../studioTypes";

export type MoveClass =
  | "surgical-order" // O1/O2 — value reorder among x-separated children
  | "band-transposition" // O3 — value reorder among shelf-banded children
  | "tail-order" // O4 — value reorder in the ordering tail
  | "remeasure-only" // O6 — level move touching no banded tier, below them all
  | "fresh-slab" // O5 — level move inside the slab prefix
  | "fresh-structural" // O7 — level move crossing a banded tier
  | "scoped-level" // O10 — level move under ONE node: that branch re-lays, nothing else; from/to are branch indices, not spine indices
  | "merge-window" // O8 — pool a block into its neighbour's window (spec §9); rebuild only, no incremental form
  | "unmerge-window"; // O9 — stand a pooled block up as its own vertical; rebuild only

export type HierarchyMove =
  | {
      kind: "value";
      scopeKey: string;
      attr: string;
      value: string;
      /** Where the value lands AMONG ITS SIBLINGS — an index into `siblings`, never into the plan-wide `valueOrder[attr]`. */
      destIndex: number;
      /** The scope's value sequence as drawn (`StudioTreeNode.siblings`); absent for callers with no tree, which fall back to the scoped-then-global order. */
      siblings?: readonly string[];
    }
  | { kind: "level"; from: number; to: number }
  | { kind: "scoped-level"; scopeNodeId: string; from: number; to: number }
  | { kind: "merge"; attr: string; value: string }
  | { kind: "unmerge"; attr: string; value: string };

/** Leading tiers whose children separate along x — the slab prefix. NOT clamped, unlike `slabDepthOf`: an all-banded spine reports zero. */
export const leadingSlabTiers = (spine: readonly SpineLevel[]): number => {
  let depth = 0;
  for (const level of spine) {
    if (level.orient !== "horizontal") break;
    depth += 1;
  }
  return depth;
};

/** Index of the last banded tier (internal "vertical"), or -1 when none bands. */
export const lastBandedTier = (spine: readonly SpineLevel[]): number => {
  for (let index = spine.length - 1; index >= 0; index--) {
    if (spine[index].orient === "vertical") return index;
  }
  return -1;
};

const classifyLevelMove = (spine: readonly SpineLevel[], from: number, to: number): MoveClass => {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  for (let tier = lo; tier <= hi; tier++) {
    if (spine[tier]?.orient === "vertical") return "fresh-structural";
  }
  if (hi < leadingSlabTiers(spine)) return "fresh-slab";
  if (lo > lastBandedTier(spine)) return "remeasure-only";
  // Unclassified gap: an interval strictly between two banded tiers, containing none. Fresh regeneration is the always-safe fallback.
  return "fresh-slab";
};

const classifyValueMove = (
  draft: StudioDraft,
  move: Extract<HierarchyMove, { kind: "value" }>,
): MoveClass => {
  const levelIndex = draft.spine.findIndex((level) => level.attr === move.attr);
  // An attribute the spine does not carry cannot move product between shelves, so replay is the safe default.
  if (levelIndex < 0) return "tail-order";
  const effective: Orientation =
    draft.orientOverrides[move.scopeKey] ?? draft.spine[levelIndex].orient;
  if (effective === "vertical") return "band-transposition";
  if (levelIndex > lastBandedTier(draft.spine)) return "tail-order";
  return "surgical-order";
};

export const classifyMove = (draft: StudioDraft, move: HierarchyMove): MoveClass => {
  if (move.kind === "level") return classifyLevelMove(draft.spine, move.from, move.to);
  if (move.kind === "scoped-level") return "scoped-level";
  if (move.kind === "merge") return "merge-window";
  if (move.kind === "unmerge") return "unmerge-window";
  return classifyValueMove(draft, move);
};
